import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getOperationName } from 'apollo-link';
import { GET_AUTHORS } from '../pages/CampaignPage/CampaignEditPage/ContentPage/ResourceList/AdvancedSearch/AuthorSelect/getAuthors.web.graphql';
import {
  GET_BRAND_CATEGORIES,
  GET_CATEGORIES,
} from '../pages/CampaignPage/CampaignEditPage/ContentPage/ResourceList/AdvancedSearch/CategorySelect/getCategories.web.graphql';
import { SEARCH_IMAGE_SOURCES_CATEGORIES } from '../pages/CampaignPage/CampaignEditPage/ContentPage/ResourceList/AdvancedSearch/ImageSourceSelect/getImageSourceCategories.web.graphql';
import { GET_SEARCH_TAGS } from '../pages/CampaignPage/CampaignEditPage/ContentPage/ResourceList/AdvancedSearch/TagsInput/getEnabledTags.web.graphql';
import { GET_QUALIFIERS } from '../pages/CampaignPage/CampaignEditPage/ContentPage/ResourceList/AdvancedSearch/getQualifers.web.graphql';
import { GET_SOURCES } from '../pages/CampaignPage/CampaignEditPage/ContentPage/ResourceList/AdvancedSearch/getSource.web.graphql';
import { auth } from '../utils/auth';
import { GET_ARTICLES } from './queries/articles.web.graphql';
import {
  GET_CATEGORIES_CHILDREN,
  GET_CATEGORY,
  GET_RECIPES_CATEGORIES,
} from './queries/categories.recipe.graphql';
import { GET_RECIPES } from './queries/recipes.recipe.graphql';
import { GET_ARTICLE_SOURCES } from './queries/sources.web.graphql';
import { GET_RECIPE_TAGS } from './queries/tags.recipe.graphql';
import { GET_ARTICLE_TAGS } from './queries/tags.web.graphql';
import {
  GET_FOCUS_POINT,
  GET_RESOLVED_IMAGE,
  GET_UPLOAD_URL,
} from './queries/uploadImage.image.graphql';
import { GET_USERS } from './queries/users.auth.graphql';

const identifyRequestLink = new ApolloLink((operation, forward) => {
  operation.setContext(() => {
    let baseUri;
    switch (operation.operationName) {
      case getOperationName(GET_USERS):
        baseUri = config.AUTH_API;
        break;
      case getOperationName(GET_ARTICLES):
      case getOperationName(GET_ARTICLE_SOURCES):
      case getOperationName(GET_ARTICLE_TAGS):
      case getOperationName(GET_AUTHORS):
      case getOperationName(GET_CATEGORIES):
      case getOperationName(GET_QUALIFIERS):
      case getOperationName(GET_SEARCH_TAGS):
      case getOperationName(GET_SOURCES):
      case getOperationName(SEARCH_IMAGE_SOURCES_CATEGORIES):
      case getOperationName(GET_BRAND_CATEGORIES):
        baseUri = config.API_ONE_WEB;
        break;
      case getOperationName(GET_RECIPES):
      case getOperationName(GET_RECIPES_CATEGORIES):
      case getOperationName(GET_CATEGORIES_CHILDREN):
      case getOperationName(GET_CATEGORY):
      case getOperationName(GET_RECIPE_TAGS):
        baseUri = config.RECIPE_API_URL;
        break;
      case getOperationName(GET_RESOLVED_IMAGE):
      case getOperationName(GET_UPLOAD_URL):
      case getOperationName(GET_FOCUS_POINT):
        baseUri = config.API_IMAGE;
        break;
      default:
        baseUri = config.API_URL;
    }
    return {
      uri: `${baseUri}?${operation.operationName}`,
    };
  });
  if (forward) {
    return forward(operation);
  }

  return null;
});

const authLink = setContext(async (_, context) => {
  const jwt = await auth.getJwt();
  if (!jwt) {
    return context;
  }
  return {
    ...context,
    headers: {
      ...context.headers,
      Authorization: `Bearer ${jwt}`,
      'x-client-name': `${config.ENV}-BONE-NEWSLETTER-REACT`,
      'x-client-version': 'BONE-NEWSLETTER-VERSION',
    },
  };
});

const fields = ['campaigns', 'newsletters', 'searchArticles', 'recipes'];

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: fields.reduce(
        (prev, fieldName) => ({
          ...prev,
          [fieldName]: {
            keyArgs: ['where', 'first', 'orderBy'],
            merge: (existing = [], incoming: any, { args }: any) =>
              !args?.skip ? incoming : [...existing, ...incoming],
          },
        }),
        {},
      ),
    },
  },
});

const httpLink = new HttpLink({ uri: config.API_URL });

export const client = new ApolloClient({
  link: ApolloLink.from([identifyRequestLink, authLink, httpLink]),
  cache,
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
  },
  name: 'ONE_NEWSLETTER',
  version: config.VERSION,
});
